import { useCallback, useEffect, useState } from 'react';

import {
  Badge,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { People as PeopleIcon } from '@material-ui/icons';
import { Virtualizer } from '@tanstack/react-virtual';
import { useModal } from 'react-modal-hook';

import { BlockedContactBadge } from 'components/BlockedContactBadge';
import { BlockedContactName } from 'components/BlockedContactName';
import CardItem from 'components/CardItem';
import { ContactAvatar } from 'components/ContactAvatar';
import DrawerHeader from 'components/DrawerHeader';
import FilterListaAtendimentos from 'components/FilterListaAtendimentos';
import ModalHistoricoAtendimentoContato from 'components/ModalDetalhesAtendimentoFila';
import SearchBar from 'components/SearchBar';
import StyledChannelChipIcon from 'components/StyledChannelChipIcon';
import VirtualizedListContainer from 'components/VirtualizedListContainer';
import { CHANNEL_ICON_CONFIG } from 'constants/channel-icon-config';
import { LOCAL_STORAGE_KEY_ORDER_BY_FILA_ESPERA } from 'constants/localStorageKeys';
import { usePersistedOrderBy } from 'hooks/usePersistedOrderBy';
import {
  useUrlFilters,
  ORDER_BY_OPTIONS,
  OrderByOption,
} from 'hooks/useUrlFilters';
import { useVirtualizedList } from 'hooks/useVirtualizedList';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtendimentoInterface,
  AtendimentoFilaInterface,
  atendimentosSelector,
  fetchListaAtendimentosNaFilaV2,
  resetListaAtendimentosNaFilaV2,
  setDrawerFilaDeEsperaAberto,
  setSearchTermFilaDeEspera,
  setOrderByFilaDeEspera,
} from 'store/reducers/atendimentos';
import { formatWaitTime } from 'utils/formatWaitTime';

// import DrawerDadosAtendimento from './DrawerDadosAtendimento';

type DrawerListaAtendimentosProps = {
  open: boolean;
  onClose: () => void;
};

const useStyles = makeStyles(({ spacing }) => ({
  drawer: {
    width: 500,
  },
  drawerPaper: {
    width: 500,
    flexShrink: 0,
    overflow: 'hidden',
  },
  list: {
    height: '100%',
    position: 'relative',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingLeft: spacing(1),
    paddingRight: spacing(1),
  },
  badge: {
    position: 'relative',
    transform: 'scale(0.9) translate(0px, 4px)',
  },
  cardItem: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));

export default function DrawerListaAtendimentos(
  props: DrawerListaAtendimentosProps,
) {
  const { open, onClose } = props;
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const {
    listaAtendimentosNaFilaV2,
    listaAtendimentosNaFilaV2Cursor,
    loadingListaAtendimentosNaFilaV2,
    orderByFilaDeEspera,
    searchTermFilaDeEspera,
  } = useAppSelector(atendimentosSelector);
  const [atendimentoSelecionado, setAtendimentoSelecionado] = useState<
    AtendimentoInterface | AtendimentoFilaInterface
  >();

  const { filterOptions, setFilterOptions } = useUrlFilters();
  const { setOrderBy: persistOrderBy } = usePersistedOrderBy(
    LOCAL_STORAGE_KEY_ORDER_BY_FILA_ESPERA,
    'orderBy',
  );

  const { parentRef, virtualItems, rowVirtualizer } = useVirtualizedList({
    items: listaAtendimentosNaFilaV2,
    hasMore: Boolean(listaAtendimentosNaFilaV2Cursor?.afterCursor),
    loading: loadingListaAtendimentosNaFilaV2,
    estimateSize: 60,
    next: onLoadNextAtendimentos,
  });

  const getAtendimentosFila = useCallback(
    (params: {
      cursor?: CursorInterface;
      search?: string;
      orderBy?: string;
    }) => {
      const paramsFiltered = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined),
      );

      dispatch(fetchListaAtendimentosNaFilaV2(paramsFiltered));
    },
    [dispatch],
  );

  const clearAtendimentos = useCallback(() => {
    dispatch(resetListaAtendimentosNaFilaV2());
  }, [dispatch]);

  const handleSetOrderBy = useCallback(
    (orderByValue: OrderByOption) => {
      dispatch(setOrderByFilaDeEspera(orderByValue));
      setFilterOptions(options => ({ ...options, orderBy: orderByValue }));
      persistOrderBy(orderByValue);
    },
    [dispatch, setFilterOptions, persistOrderBy],
  );

  const handleSearch = (value: string) => {
    setFilterOptions(options => ({ ...options, search: value }));
    dispatch(setSearchTermFilaDeEspera(value));
  };

  const handleCancelSearch = () => {
    setFilterOptions(options => ({ ...options, search: undefined }));
    dispatch(setSearchTermFilaDeEspera(undefined));
  };

  // Carrega e atualiza a lista de atendimentos
  useEffect(() => {
    if (open) {
      clearAtendimentos();
      getAtendimentosFila({
        orderBy: orderByFilaDeEspera || ORDER_BY_OPTIONS.DEFAULT,
        search: searchTermFilaDeEspera,
      });
    }
  }, [
    open,
    orderByFilaDeEspera,
    searchTermFilaDeEspera,
    getAtendimentosFila,
    clearAtendimentos,
  ]);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const [showDrawerDadosAtendimento, hideDrawerDadosAtendimento] = useModal(
    ({ in: open }) => {
      if (!atendimentoSelecionado) return null;

      return (
        // <DrawerDadosAtendimento
        //   open={openDrawerDetalhes}
        //   onClose={hideDrawerDetalhes}
        //   atendimento={atendimentoSelecionado}
        // />
        <ModalHistoricoAtendimentoContato
          atendimento={atendimentoSelecionado}
          open={open}
          onClose={hideDrawerDadosAtendimento}
        />
      );
    },
    [atendimentoSelecionado],
  );

  const handleListItemClick = (
    atendimento: AtendimentoInterface | AtendimentoFilaInterface,
  ) => {
    setAtendimentoSelecionado(atendimento);
    showDrawerDadosAtendimento();
    handleClose();
  };

  function onLoadNextAtendimentos() {
    const { afterCursor } = listaAtendimentosNaFilaV2Cursor;
    if (afterCursor) {
      getAtendimentosFila({
        cursor: { afterCursor },
        search: searchTermFilaDeEspera,
        orderBy: orderByFilaDeEspera || ORDER_BY_OPTIONS.DEFAULT,
      });
    }
  }

  useEffect(() => {
    dispatch(setDrawerFilaDeEsperaAberto(open));

    return () => {
      dispatch(setDrawerFilaDeEsperaAberto(false));
      dispatch(setSearchTermFilaDeEspera(undefined));
    };
  }, [open, dispatch]);
  return (
    <Drawer
      open={open}
      onClose={handleClose}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      anchor="right"
      BackdropProps={{
        onExited: () => {
          clearAtendimentos();
          setFilterOptions(options => ({ ...options, search: undefined }));
        },
      }}
    >
      <DrawerHeader
        title="Todos os atendimentos na fila de espera"
        onClose={handleClose}
      />

      <Box style={{ paddingInline: 16, paddingTop: 16 }}>
        <Box display="flex" alignItems="center" gridGap={8}>
          <Box flexGrow={1}>
            <SearchBar
              placeholder="Procurar por nome do contato ou mensagem..."
              onChange={handleSearch}
              onCancelSearch={handleCancelSearch}
            />
          </Box>

          <FilterListaAtendimentos
            filteredOptions={{ ...filterOptions, orderBy: orderByFilaDeEspera }}
            handleSetOrderBy={handleSetOrderBy}
          />
        </Box>
      </Box>

      <List
        id="listAtendimentosNaFila"
        className={classes.list}
        ref={parentRef}
      >
        <VirtualizedListContainer
          virtualItems={virtualItems}
          virtualizer={rowVirtualizer}
          loading={loadingListaAtendimentosNaFilaV2}
          empty={
            filterOptions.search
              ? {
                  title: 'Tem certeza que digitou certo!?',
                  description: `Não encontramos nenhum contato ou atendimento com "${filterOptions.search}"`,
                }
              : {
                  title: 'Sem atendimentos',
                  description:
                    'Nenhum atendimento foi encontrado na fila de espera',
                }
          }
        >
          {virtualItems.map(virtualRow => {
            const atendimento = listaAtendimentosNaFilaV2[virtualRow.index];

            if (!atendimento) return null;

            return (
              <AtendimentoFilaItem
                key={atendimento.id}
                atendimento={atendimento}
                index={virtualRow.index}
                virtualizer={rowVirtualizer}
                onClick={handleListItemClick}
              />
            );
          })}
        </VirtualizedListContainer>
      </List>
    </Drawer>
  );
}

const AtendimentoFilaItem = ({
  atendimento,
  index,
  virtualizer,
  onClick,
}: {
  atendimento: AtendimentoInterface | AtendimentoFilaInterface;
  index: number;
  virtualizer: Virtualizer<HTMLUListElement, Element>;
  onClick: (
    atendimento: AtendimentoInterface | AtendimentoFilaInterface,
  ) => void;
}) => {
  const classes = useStyles();

  const isV2 = 'contact' in atendimento;
  const criadoEm = isV2
    ? (atendimento as AtendimentoFilaInterface).startedIn
    : (atendimento as AtendimentoInterface).criadoEm;

  const [tempoNaFila, setTempoNaFila] = useState(formatWaitTime(criadoEm));

  useEffect(() => {
    const interval = setInterval(() => {
      setTempoNaFila(formatWaitTime(criadoEm));
    }, 1000);
    return () => clearInterval(interval);
  }, [criadoEm]);

  const contact = isV2
    ? (atendimento as AtendimentoFilaInterface).contact
    : (atendimento as AtendimentoInterface).contato;

  const channel = isV2
    ? (atendimento as AtendimentoFilaInterface).channel
    : (atendimento as AtendimentoInterface).canal;

  const team = isV2
    ? (atendimento as AtendimentoFilaInterface).team
    : (atendimento as AtendimentoInterface).equipe;

  const { totalMensagensNaoLidas } = atendimento;

  const isBlocked = Boolean(
    'blockedAt' in contact
      ? contact.blockedAt || contact.bloqueadoEm
      : (contact as { blockedAt?: string | null; bloqueadoEm?: string | null })
          .blockedAt ||
          (
            contact as {
              blockedAt?: string | null;
              bloqueadoEm?: string | null;
            }
          ).bloqueadoEm,
  );

  const phoneNumber = isV2
    ? (atendimento as AtendimentoFilaInterface).contact.identifier
    : (atendimento as AtendimentoInterface).contato.contato;
  return (
    <div
      ref={virtualizer.measureElement}
      data-index={index}
      style={{ paddingBottom: 8 }}
    >
      <CardItem
        className={classes.cardItem}
        onSelect={() => onClick(atendimento)}
      >
        <ListItem disableGutters dense>
          <Typography
            variant="subtitle1"
            style={{
              fontWeight: 'bold',
              marginRight: 12,
              textAlign: 'right',
              minWidth: 30,
            }}
          >{`${index + 1}º`}</Typography>

          <ListItemAvatar style={{ minWidth: 0, marginRight: 12 }}>
            <BlockedContactBadge
              isBlocked={isBlocked}
              defaultBadgeContent={
                <Badge
                  color="error"
                  overlap="rectangular"
                  badgeContent={
                    totalMensagensNaoLidas === -1 ? '' : totalMensagensNaoLidas
                  }
                  classes={{ badge: classes.badge }}
                />
              }
            >
              <ContactAvatar
                src={
                  'foto' in contact
                    ? contact.foto
                    : (contact as { foto?: string }).foto
                }
              />
            </BlockedContactBadge>
          </ListItemAvatar>

          <ListItemText
            primary={
              <Box
                display="flex"
                alignItems="baseline"
                flexWrap="wrap"
                gridGap={4}
              >
                <BlockedContactName
                  name={'nome' in contact ? contact.nome : contact.name}
                  isBlocked={isBlocked}
                  style={{ maxWidth: '100%' }}
                >
                  {phoneNumber && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{
                        fontWeight: 'normal',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      ({phoneNumber})
                    </Typography>
                  )}
                </BlockedContactName>
              </Box>
            }
            secondary={
              <Box component="span" display="flex" flexDirection="column">
                <Box display="flex" alignItems="center" gridGap={12}>
                  <Box display="flex" alignItems="center" gridGap={4}>
                    <StyledChannelChipIcon
                      type={
                        ('type' in channel
                          ? channel.type
                          : channel.tipoCanal) as keyof typeof CHANNEL_ICON_CONFIG
                      }
                      label={'name' in channel ? channel.name : channel.nome}
                    />
                  </Box>

                  {team && (
                    <Box display="flex" alignItems="center">
                      <PeopleIcon style={{ fontSize: 16, marginRight: 4 }} />
                      <Typography
                        variant="caption"
                        component="span"
                        color="textSecondary"
                      >
                        {'name' in team ? team.name : team.nome}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            }
            primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
            secondaryTypographyProps={{
              noWrap: true,
              variant: 'caption',
              component: 'span',
            }}
          />

          <Box
            mx={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gridGap={8}
          >
            <Typography
              variant="caption"
              component="span"
              color="textSecondary"
              style={{ textAlign: 'left' }}
            >
              {tempoNaFila}
            </Typography>
          </Box>
        </ListItem>
      </CardItem>
    </div>
  );
};
